import { useState, useEffect } from 'react';
import Button from 'components/atoms/Button';
import Icon from 'components/atoms/Icon';
import Input from 'components/molecules/Input';
import Logo from 'components/molecules/Logo';
import { login, loginWithGoogle, forgotPassword, setLoggedOutCookie } from 'actions/auth';
import { emailRegex } from 'utils/index';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { IS_LOCAL } from 'actions/utils';
import { closeModal } from 'actions/modal';
import TosPrivacyLinks from 'components/molecules/TosPrivacyLinks';
import { useThunkDispatch } from 'utils/reduxHook';
import Typography from 'components/atoms/Typography';
import { HrWithText } from 'components/atoms/HrWithText';

function Login() {
  const paramEmail = useParams<{ email: string }>().email;
  const [email, setEmail] = useState(paramEmail || (IS_LOCAL ? 'eng+user1@gloo.us' : '') || '');
  const [password, setPassword] = useState((IS_LOCAL ? 'password' : '') || '');
  const [emailErrorMsg, setEmailErrorMsg] = useState('');
  const [passwordErrorMsg, setPasswordErrorMsg] = useState('');
  const dispatch = useThunkDispatch();

  useEffect(() => {
    if (emailErrorMsg) {
      if (emailRegex.test(email)) {
        setEmailErrorMsg('');
      }
    }
  }, [email]);

  useEffect(() => {
    if (passwordErrorMsg) {
      if (password.length >= 8) {
        setPasswordErrorMsg('');
      }
    }
  }, [password]);

  async function handleGoogleLogin() {
    try {
      setLoggedOutCookie('no');
      await dispatch(loginWithGoogle());
      handleLoginRedirect();
    } catch (error) {
      console.error(error);
      setLoggedOutCookie('yes');
    }
  }

  useEffect(() => {
    // Make sure all modals get closed when we land on the login page
    dispatch(closeModal());
  }, []);

  async function handleLogin() {
    if (!emailRegex.test(email)) {
      const emailError = 'Please enter a valid email address';
      setEmailErrorMsg(emailError);
      toast.warning(emailError);
      return;
    }
    if (password.length < 8) {
      const passwordError = 'Please enter a password of 8 characters or more';
      setPasswordErrorMsg(passwordError);
      toast.warning(passwordError);
      return;
    }
    try {
      setLoggedOutCookie('no');
      await dispatch(login(email.toLowerCase(), password));
      handleLoginRedirect();
    } catch (error) {
      console.error(error);
      setLoggedOutCookie('yes');
    }
  }

  function handleLoginRedirect() {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const loginRedirectPathname = urlSearchParams.get('redirect');
    if (loginRedirectPathname) {
      urlSearchParams.delete('redirect');
      const redirectSearchParams = urlSearchParams.size ? `?${urlSearchParams.toString()}` : '';
      window.location.assign(`${loginRedirectPathname}${redirectSearchParams}`);
    }
  }

  async function handleForgotPassword() {
    if (!email) {
      toast.error('Please enter your email first');
      return;
    }
    await dispatch(forgotPassword(email.toLowerCase()));
  }

  return (
    <section className="bg-white h-full w-full">
      <div className="max-w-2xl col-span-4 md:col-span-2 col-span-start-2 justify-center mx-auto pt-8 md:pt-0 px-8 md:px-24 lg:px-32">
        <Logo size="large" className="pt-6 md:pt-12 mb-2 md:mb-6 items-start" />

        <Typography variant="h2">Welcome back!</Typography>

        <div className="flex flex-col pt-6 md:pt-8">
          <div className="text-center">
            <Button
              type="white"
              dataDurableId="googleLoginButton"
              className="w-full text-sm inline-flex items-center justify-center"
              onClick={handleGoogleLogin}
            >
              <Icon name="google" className="w-5 mr-1" />
              Sign in with Google
            </Button>
          </div>

          <HrWithText text="or" />

          <div className="flex flex-col pt-4">
            <Input
              autoFocus
              className="bg-charcoal-50"
              placeholder="your@email.com"
              onChangeText={(t) => setEmail(t)}
              value={email}
              error={emailErrorMsg}
              label="Email"
              onEnter={handleLogin}
            />
          </div>
          <div className="flex flex-col pt-4">
            <Input
              className="bg-charcoal-50"
              placeholder="Password"
              onChangeText={(t) => setPassword(t)}
              value={password}
              error={passwordErrorMsg}
              type="password"
              label="Password"
              onEnter={handleLogin}
            />
          </div>
          <div className="pt-4 w-full text-right">
            <Button
              text="Forgot Password?"
              type="transparent"
              dataDurableId="forgotPasswordButton"
              onClick={handleForgotPassword}
              className="font-medium text-primary-700 text-sm"
            />
          </div>
          <Button
            type="primary"
            text="Log In"
            dataDurableId="loginButton"
            onClick={handleLogin}
            className="p-2 mt-8 justify-center"
          />
        </div>

        <div className="w-full text-sm font-medium text-left pt-6 pb-12">
          <TosPrivacyLinks type="signin" />
        </div>
      </div>
    </section>
  );
}

export default Login;
